import { RoutePending, ShellLayout } from "@repo/app-shell"
import { api } from "@repo/db"
import type { StatsigUser } from "@statsig/react-bindings"
import { Outlet, createFileRoute, redirect, type AnyRouteMatch } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

import { FeedbackWidget } from "@/components/feedback/feedback-widget"
import { AppSidebar } from "@/components/mockUI/app-sidebar"
import { SettingsModalProvider } from "@/components/settings/settings-modal"
import { SupportChatWidget } from "@/components/support-chat/support-chat-widget"
import { DealerSiteHeader } from "@/components/ui/dealer-site-header"
import { buildDealershipStatsigUser } from "@/features/statsig/user"
import { convexClient, queryWithTimeout } from "@/lib/convex"
import { useTowerStatsig } from "@/providers/StatsigProvider"

export const Route = createFileRoute("/$dealership")({
  staticData: {
    breadcrumb: ({ match }: { match: AnyRouteMatch }) => {
      const context = match.context
      const dealership =
        context && typeof context === "object" && "dealership" in context
          ? (context as { dealership?: { name?: string } | null }).dealership
          : null
      return dealership?.name ?? "Dealership"
    },
  },
  beforeLoad: async (ctx) => {
    const { dealership: slug } = ctx.params
    const user = await queryWithTimeout(convexClient.query(api.auth.getCurrentUser))
    if (!user) {
      throw redirect({
        to: "/login",
        search: { redirect: ctx.location.pathname },
      })
    }

    const isSuperAdmin = user.isSuperAdmin ?? false

    // For regular users, route slug must match their assigned dealership.
    if (!isSuperAdmin) {
      if (!user.dealership) {
        throw redirect({ to: "/", search: { redirect: undefined } })
      }
      if (user.dealership.slug !== slug) {
        throw redirect({
          to: "/$dealership/dashboard",
          params: { dealership: user.dealership.slug },
        })
      }
    }

    // Use a no-context lookup here, then establish/verify active dealership separately.
    const dealership = await queryWithTimeout(
      convexClient.query(api.dealerships.queries.getBySlugNoAuth, { slug }),
    )

    if (!dealership) {
      return { dealership: null, stores: [], user, isSuperAdmin }
    }

    // Super admins can deep-link by slug; ensure active dealership context is aligned.
    if (isSuperAdmin && user.dealership?.id !== dealership._id) {
      await convexClient.mutation(api.users.mutations.setActiveDealership, {
        dealershipId: dealership._id,
      })
    }

    const stores = await queryWithTimeout(convexClient.query(api.stores.queries.listStores, {}))
    return { dealership, stores, user, isSuperAdmin }
  },
  component: DealershipLayout,
})

function DealershipLayout() {
  const { dealership, isSuperAdmin, user } = Route.useRouteContext()
  const { dealership: slug } = Route.useParams()
  const isStatsigReady = useDealershipStatsigSync({
    dealership,
    isSuperAdmin,
    user,
  })

  if (!dealership) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold">Dealership not found</h1>
          <p className="text-muted-foreground mt-2">No dealership found for slug "{slug}".</p>
          <a
            href="/"
            className="mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 text-primary-foreground"
          >
            Back to the home page.
          </a>
        </div>
      </div>
    )
  }

  if (!isStatsigReady) {
    return <RoutePending />
  }

  return (
    <SettingsModalProvider
      dealershipId={dealership._id}
      dealershipUrl={dealership.website}
      userId={user?.userId ?? undefined}
    >
      <ShellLayout sidebar={<AppSidebar variant="inset" />} header={<DealerSiteHeader />}>
        <Outlet />
      </ShellLayout>
      <SupportChatWidget />
      <FeedbackWidget />
    </SettingsModalProvider>
  )
}

function useDealershipStatsigSync({
  dealership,
  isSuperAdmin,
  user,
}: {
  dealership: {
    _id: string
    betterAuthOrgId?: string | null
    name: string
    slug: string
  } | null
  isSuperAdmin: boolean
  user: {
    email?: string | null
    isDemo?: boolean | null
    name?: string | null
    userId?: string | null
    dealership?: {
      id?: string | null
      role?: string | null
      slug?: string | null
    } | null
  }
}) {
  const { configured, syncUserAsync } = useTowerStatsig()
  const targetUser = useMemo<StatsigUser | null>(
    () =>
      dealership
        ? buildDealershipStatsigUser({
            dealership,
            isSuperAdmin,
            user,
          })
        : null,
    [dealership, isSuperAdmin, user],
  )
  const targetKey = targetUser ? JSON.stringify(targetUser) : null
  const [syncedKey, setSyncedKey] = useState<string | null>(targetKey)

  useEffect(() => {
    if (!configured || !targetUser || !targetKey) {
      setSyncedKey(targetKey)
      return
    }

    if (syncedKey === targetKey) {
      return
    }

    let cancelled = false
    void syncUserAsync(targetUser).then(() => {
      if (!cancelled) {
        setSyncedKey(targetKey)
      }
    })

    return () => {
      cancelled = true
    }
  }, [configured, syncedKey, syncUserAsync, targetKey, targetUser])

  return !configured || !targetKey || syncedKey === targetKey
}
